import React, { useEffect, useState } from 'react';

const Preloader = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Initial state
  // Phase 1: Logo appears
  // Phase 2: Logo fully visible + glow
  // Phase 3: Fade out

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 100),      // Start animation
      setTimeout(() => setPhase(2), 1200),     // Logo glow
      setTimeout(() => setPhase(3), 2400),     // Start fade out
      setTimeout(() => onComplete?.(), 3200),  // Complete
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black transition-opacity duration-700 ${
        phase >= 3 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 bg-accent/30 rounded-full transition-all duration-1000 ${
              phase >= 1 ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animation: phase >= 1 ? `float ${3 + Math.random() * 2}s ease-in-out infinite` : 'none',
            }}
          />
        ))}
      </div>

      {/* Radial gradient background */}
      <div 
        className={`absolute inset-0 transition-opacity duration-1000 ${
          phase >= 2 ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'radial-gradient(circle at center, rgba(0, 255, 209, 0.05) 0%, transparent 50%)',
        }}
      />

      {/* Logo container */}
      <div className="relative">
        {/* Glow effect behind logo */}
        <div
          className={`absolute inset-0 blur-3xl transition-all duration-1000 ${
            phase >= 2 ? 'opacity-100 scale-150' : 'opacity-0 scale-100'
          }`}
          style={{
            background: 'radial-gradient(circle, rgba(255, 107, 0, 0.3) 0%, transparent 70%)',
          }}
        />

        {/* Main logo */}
        <div
          className={`relative transition-all duration-1000 ease-out ${
            phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
        >
          {/* Logo text */}
          <h1 className="text-5xl sm:text-7xl font-semibold tracking-tight select-none">
            <span
              className={`inline-block transition-all duration-700 ${
                phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ 
                color: '#FF6B00',
                transitionDelay: '200ms',
                textShadow: phase >= 2 ? '0 0 40px rgba(255, 107, 0, 0.5)' : 'none',
              }}
            >
              W
            </span>
            <span
              className={`inline-block text-white transition-all duration-700 ${
                phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              eby
            </span>
            <span
              className={`inline-block text-white transition-all duration-700 ${
                phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              see
            </span>
            <span
              className={`inline-block text-accent transition-all duration-700 ${
                phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
              }`}
              style={{ 
                transitionDelay: '600ms',
                textShadow: phase >= 2 ? '0 0 20px rgba(0, 255, 209, 0.8)' : 'none',
              }}
            >
              .
            </span>
          </h1>

          {/* Animated underline */}
          <div className="relative h-[2px] mt-4 overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF6B00] via-accent to-[#FF6B00] transition-all duration-1000 ease-out ${
                phase >= 1 ? 'w-full' : 'w-0'
              }`}
              style={{ transitionDelay: '700ms' }}
            />
          </div>

          {/* Tagline */}
          <p
            className={`text-white/50 text-sm sm:text-base mt-4 text-center tracking-widest uppercase transition-all duration-700 ${
              phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Agence Web Premium
          </p>
        </div>

        {/* Loading indicator */}
        <div
          className={`absolute -bottom-12 left-1/2 -translate-x-1/2 transition-all duration-500 ${
            phase >= 1 && phase < 3 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-accent/60 rounded-full"
                style={{
                  animation: 'pulse-dot 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Corner accents */}
      <div className={`absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-white/10 transition-all duration-1000 ${
        phase >= 1 ? 'opacity-100' : 'opacity-0'
      }`} />
      <div className={`absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-white/10 transition-all duration-1000 ${
        phase >= 1 ? 'opacity-100' : 'opacity-0'
      }`} />
      <div className={`absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-white/10 transition-all duration-1000 ${
        phase >= 1 ? 'opacity-100' : 'opacity-0'
      }`} />
      <div className={`absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white/10 transition-all duration-1000 ${
        phase >= 1 ? 'opacity-100' : 'opacity-0'
      }`} />

      {/* Custom animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) scale(1.5);
            opacity: 0.6;
          }
        }
        
        @keyframes pulse-dot {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Preloader;
